import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useToken from "../../../../../hooks/auth/useToken";
import { chatService } from "../../../services/chatService";
import {
  Home,
  Users,
  CreditCard,
  Settings,
  Globe,
  Bell,
  ArrowRightLeft,
  Wallet,
  MessageCircle,
  TrendingUp,
  ShoppingBag,
  ChevronDown,
  Award,
  ClipboardList,
  Megaphone,
  Inbox,
  Mail,
  LucideIcon,
  ListTodo,
  Send,
} from "lucide-react";

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

interface MenuItem {
  id?: string;
  icon: LucideIcon;
  label: string;
  route?: string;
  badge?: string | number | null;
  children?: { label: string; route: string; icon?: LucideIcon; badge?: number | null }[];
}

const formatBadge = (count: number | string | null | undefined): string | null => {
  if (count === null || count === undefined || count === "") return null;
  const n = typeof count === "number" ? count : Number(count);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 99 ? "99+" : String(n);
};

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useToken();
  const [chatUnread, setChatUnread] = useState(0);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>(() => ({
    operations: ["/transactions", "/bank-transferts", "/wallets", "/interac-emails", "/country-transferts"].some((r) =>
      location.pathname.includes(r)
    ),
    communication: ["/system-announcements", "/notifications", "/chat"].some((r) => location.pathname.includes(r)),
    "profit-tracking": location.pathname.includes("/profit-tracking"),
  }));

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const refresh = async () => {
      try {
        const count = await chatService.getUnreadCount(token);
        if (!cancelled) setChatUnread(Number(count) || 0);
      } catch {
        /* Le badge n'est pas bloquant. */
      }
    };

    refresh();
    const id = window.setInterval(refresh, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token, location.pathname]);

  const menuItems: MenuItem[] = [
    { icon: Home, label: "Dashboard", route: "/dashboard", badge: null },
    { icon: Users, label: "Users/KYC", route: "/users", badge: "12" },
    { icon: Globe, label: "Pays", route: "/countries", badge: null },
    {
      id: "operations",
      icon: CreditCard,
      label: "Opérations",
      badge: null,
      children: [
        { label: "Transactions", route: "/transactions", icon: CreditCard },
        { label: "Transferts Bancaires", route: "/bank-transferts/canada-cmr", icon: ArrowRightLeft },
        { label: "Wallets", route: "/wallets", icon: Wallet },
        { label: "Emails Interac", route: "/interac-emails", icon: Mail },
        { label: "Transferts Pays", route: "/country-transferts", icon: ArrowRightLeft },
        { label: "Transferts Chine", route: "/cameroun-chine", icon: Send },
      ],
    },
    { icon: ClipboardList, label: "Journal d'activité", route: "/audit-logs", badge: null },
    { icon: ListTodo, label: "Gestion des taches", route: "/tasks", badge: null },

    {
      id: "communication",
      icon: Megaphone,
      label: "Communication",
      badge: chatUnread || null,
      children: [
        { label: "Annonces Système", route: "/system-announcements", icon: Bell },
        { label: "Notifications", route: "/notifications", icon: Bell },
        { label: "Chat Support", route: "/chat", icon: MessageCircle, badge: chatUnread || null },
      ],
    },
    {
      id: "profit-tracking",
      icon: TrendingUp,
      label: "Suivi et Bénéfices",
      badge: null,
      children: [
        { label: "Ambassadeurs", route: "/profit-tracking/ambassadors", icon: Award },
        { label: "Suivi des bénéfices", route: "/profit-tracking", icon: TrendingUp },
      ],
    },
    { icon: ShoppingBag, label: "Buy and Sell", route: "/buy-and-sell", badge: null },
    { icon: Inbox, label: "Demandes de retrait", route: "/withdrawal-requests", badge: null },
    { icon: Settings, label: "Paramètres", route: "/settings", badge: null },
  ];

  const handleNavigation = (route: string) => {
    navigate(`/izilife-pay${route}`);
  };

  const isActiveRoute = (route: string) => {
    if (route === "/profit-tracking") {
      return location.pathname.endsWith("/profit-tracking") || location.pathname.endsWith("/profit-tracking/");
    }
    if (route.startsWith("/bank-transferts")) {
      return location.pathname.includes("/bank-transferts");
    }
    return location.pathname.includes(route);
  };

  const isParentActive = (item: MenuItem) => {
    if (item.route) return isActiveRoute(item.route);
    return item.children?.some((child) => isActiveRoute(child.route)) ?? false;
  };

  const toggleMenu = (key: string) => {
    setExpandedMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div
      className={`bg-white border-r border-gray-200 transition-all duration-300 ${isCollapsed ? "w-16" : "w-64"} h-full flex flex-col`}
    >
      <div className="p-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div className={`flex ${isCollapsed ? "justify-center w-full" : "items-center space-x-2"}`}>
            <img src="/IZILIFEPAY_icon.png" alt="Kafintech" className="w-8 h-8 object-contain" />
            {!isCollapsed && (
              <div className="flex flex-col">
                <span className="font-bold text-gray-800">Izilife Pay</span>
                <span className="text-xs text-gray-500">Gestion de l'appli</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            const hasChildren = !!item.children?.length;
            const menuKey = item.id || item.route || `group-${index}`;
            const isExpanded = expandedMenus[menuKey] ?? false;
            const isActive = isParentActive(item);

            if (hasChildren) {
              return (
                <li key={menuKey}>
                  <button
                    onClick={() => {
                      if (isCollapsed) {
                        handleNavigation(item.children![0].route);
                      } else {
                        toggleMenu(menuKey);
                      }
                    }}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                      isActive
                        ? "text-green-700 bg-green-50 border-r-2 border-green-600"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <Icon className="w-5 h-5" />
                      {isCollapsed && formatBadge(item.badge) && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                      )}
                    </div>
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        {formatBadge(item.badge) && (
                          <span className="px-1.5 py-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full min-w-[18px] text-center">
                            {formatBadge(item.badge)}
                          </span>
                        )}
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                      </>
                    )}
                  </button>
                  {!isCollapsed && isExpanded && (
                    <ul className="mt-1 ml-4 space-y-1">
                      {item.children!.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = isActiveRoute(child.route);
                        return (
                          <li key={child.route}>
                            <button
                              onClick={() => handleNavigation(child.route)}
                              className={`w-full flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                childActive
                                  ? "text-green-700 bg-green-50 font-medium"
                                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                              }`}
                            >
                              {ChildIcon && <ChildIcon className="w-4 h-4" />}
                              <span className="flex-1 text-left">{child.label}</span>
                              {formatBadge(child.badge) && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full min-w-[18px] text-center">
                                  {formatBadge(child.badge)}
                                </span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            }

            return (
              <li key={menuKey}>
                <button
                  onClick={() => handleNavigation(item.route!)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? "text-green-700 bg-green-50 border-r-2 border-green-600"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                  }`}
                >
                  <Icon className="flex-shrink-0 w-5 h-5" />
                  {!isCollapsed && (
                    <>
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.badge && (
                        <span className="px-2 py-1 text-xs text-white bg-red-500 rounded-full">
                          {formatBadge(item.badge) || item.badge}
                        </span>
                      )}
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};
